import { TextInfo } from '@evgo/react-material-components';
import {
  Button,
  Checkbox,
  FormHelperText,
  IconButton,
  Table,
  TableBody,
  TableCell,
  TableCellProps,
  TableHead,
  TableRow,
  Tooltip,
} from '@material-ui/core';
import {
  AddCircleOutline as AddCircleOutlineIcon,
  Error as ErrorIcon,
  RemoveCircleOutline as RemoveCircleOutlineIcon,
} from '@material-ui/icons';
import { useFormikContext } from 'formik';
import _ from 'lodash';
import React, { Fragment, useCallback } from 'react';
import days from '../../../../lib/fixtures/days.json';
import defaultPlanElementHour from '../../../../lib/fixtures/defaultPlanElementHour.json';
import { Styled } from './styles';

export interface Props {
  className?: string;
  id?: string;
  path: string;
  readOnly: boolean;
}

type TableColumnKeys =
  | 'startTime'
  | 'endTime'
  | 'discount'
  | 'maxDuration'
  | 'filler'
  | 'monday'
  | 'tuesday'
  | 'wednesday'
  | 'thursday'
  | 'friday'
  | 'saturday'
  | 'sunday'
  | 'deleteAction';

interface TableColumnsSettings {
  key: TableColumnKeys;
  label: string;
  width: string;
  align: TableCellProps['align'];
}

export const ElementHours: React.FC<Props> = ({ className: parentClass, id: parentId, path, readOnly }) => {
  const i = Number(_.join(_.initial(_.head(_.reverse(_.split(path, '[')))), ''));
  // const id = _.kebabCase('ElementHours');
  const className = 'element-hours-component';
  const { errors, handleBlur, handleChange, isSubmitting, setFieldValue, values } = useFormikContext();
  const columns = [
    { key: 'startTime', label: 'Start Time *', width: '20%', align: 'left' },
    { key: 'endTime', label: 'End Time *', width: '20%', align: 'left' },
    { key: 'discount', label: 'Discount', width: '15%', align: 'left' },
    { key: 'maxDuration', label: 'Max Duration (min)', width: '27%', align: 'left' },
    { key: 'filler', label: '', width: '10%', align: 'center' },
    { key: 'monday', label: 'M', width: '1%', align: 'center' },
    { key: 'tuesday', label: 'Tu', width: '1%', align: 'center' },
    { key: 'wednesday', label: 'W', width: '1%', align: 'center' },
    { key: 'thursday', label: 'Th', width: '1%', align: 'center' },
    { key: 'friday', label: 'F', width: '1%', align: 'center' },
    { key: 'saturday', label: 'S', width: '1%', align: 'center' },
    { key: 'sunday', label: 'Su', width: '1%', align: 'center' },
    { key: 'deleteAction', label: '', width: '1%', align: 'center' },
  ] as TableColumnsSettings[];
  let customClass = className;

  if (parentClass) customClass += ` ${parentClass}`;

  // Handles adding a custom hour to the current rate
  const handleAddHour = useCallback(() => {
    const { hours } = _.get(values, path);
    const value = [...hours, defaultPlanElementHour];

    setFieldValue(`${path}.hours`, value);
  }, [values, path, setFieldValue]);

  // Handles changing end time
  const handleChangeEndTime = useCallback(
    (event) => {
      const {
        target: { name, value: oldValue },
      } = event;
      const value = `${oldValue}:59`;

      setFieldValue(name, value);
    },
    [setFieldValue],
  );

  // Handles changing start time
  const handleChangeStartTime = useCallback(
    (event) => {
      const {
        target: { name, value: oldValue },
      } = event;
      const value = `${oldValue}:00`;

      setFieldValue(name, value);
    },
    [setFieldValue],
  );

  // Handles removing the current custom from the current rate
  const handleRemoveHour = useCallback(
    (j) => () => {
      const { hours } = _.get(values, path);
      const value = [...hours];

      _.pullAt(value, [j]);
      setFieldValue(`${path}.hours`, value);
    },
    [path, setFieldValue, values],
  );

  type ColumnErrors = {
    [key in TableColumnKeys]: string;
  };

  const columnErrors = {
    startTime: '',
    endTime: '',
    discount: '',
    maxDuration: '',
  } as ColumnErrors;

  if (errors && _.get(errors, `${path}.hours`)) {
    _.forEach(_.get(errors, `${path}.hours`), (error) => {
      if (error) {
        if (error.startTime) columnErrors.startTime = error.startTime;
        if (error.endTime) columnErrors.endTime = error.endTime;
        if (error.discount) columnErrors.discount = error.discount;
        if (error.maxDuration) columnErrors.maxDuration = error.maxDuration;
      }
    });
  }

  return (
    <Styled>
      <Table id={parentId} className={customClass}>
        <colgroup>
          {columns.map(({ key, width }) => (
            <col key={key} style={{ width }} />
          ))}
        </colgroup>

        <TableHead className={className}>
          <TableRow className={className}>
            {columns.map((column) => (
              <TableCell
                align={column.align as TableCellProps['align']}
                className={className}
                component="th"
                key={column.key}
              >
                {column.label}
                {columnErrors && columnErrors[column.key] && (
                  <Tooltip
                    className={className}
                    title={columnErrors[column.key]}
                    enterDelay={200}
                    leaveDelay={200}
                    placement="top"
                    id={`yy-${i}-column-${column.key}-error-tooltip`}
                  >
                    <ErrorIcon color="error" />
                  </Tooltip>
                )}
              </TableCell>
            ))}
          </TableRow>
        </TableHead>

        <TableBody>
          {_.get(values, `${path}.hours`, []).map((row: Record<string, unknown>, j: number) => (
            <TableRow key={j}>
              <TableCell className={className}>
                <TextInfo
                  className={className}
                  disabled={isSubmitting || readOnly}
                  // id={ `${id}-${i}-hour-${j}-start-time-input` }
                  id={`yy-${i}-hour-${j}-start-time-input`}
                  inputProps={{ className, step: 60 }}
                  name={`${path}.hours[${j}].startTime`}
                  onBlur={handleBlur}
                  onChange={handleChangeStartTime}
                  type="time"
                  error={!!_.get(errors, `${path}.hours[${j}].startTime`)}
                  value={_.join(_.split(_.get(values, `${path}.hours[${j}].startTime`), ':', 2), ':')}
                  data-testid={`plan-elements-yy-${i}-hour-${j}-start-time-input`}
                />
              </TableCell>

              <TableCell className={className}>
                <TextInfo
                  className={className}
                  disabled={isSubmitting || readOnly}
                  // id={ `${id}-${i}-hour-${j}-end-time-input` }
                  id={`yy-${i}-hour-${j}-end-time-input`}
                  inputProps={{ className, step: 60 }}
                  name={`${path}.hours[${j}].endTime`}
                  onBlur={handleBlur}
                  onChange={handleChangeEndTime}
                  type="time"
                  error={!!_.get(errors, `${path}.hours[${j}].endTime`)}
                  value={_.join(_.split(_.get(values, `${path}.hours[${j}].endTime`), ':', 2), ':')}
                  data-testid={`plan-elements-yy-${i}-hour-${j}-end-time-input`}
                />
              </TableCell>

              <TableCell className={className}>
                <TextInfo
                  className={className}
                  disabled={isSubmitting || readOnly}
                  // id={ `${id}-${i}-hour-${j}-discount-input` }
                  id={`yy-${i}-hour-${j}-discount-input`}
                  name={`${path}.hours[${j}].discount`}
                  onBlur={handleBlur}
                  onChange={handleChange}
                  adornmentEnd={<div className="adornment">%</div>}
                  error={!!_.get(errors, `${path}.hours[${j}].discount`)}
                  value={_.get(values, `${path}.hours[${j}].discount`)}
                  data-testid={`plan-elements-yy-${i}-hour-${j}-discount-input`}
                />
              </TableCell>

              <TableCell className={className}>
                <TextInfo
                  className={className}
                  disabled={isSubmitting || readOnly}
                  // id={ `${id}-${i}-hour-${j}-max-duration-input` }
                  id={`yy-${i}-hour-${j}-maxDuration-input`}
                  name={`${path}.hours[${j}].maxDuration`}
                  onBlur={handleBlur}
                  onChange={handleChange}
                  error={!!_.get(errors, `${path}.hours[${j}].maxDuration`)}
                  value={_.get(values, `${path}.hours[${j}].maxDuration`)}
                  data-testid={`plan-elements-yy-${i}-hour-${j}-maxDuration-input`}
                />
              </TableCell>

              <TableCell className={className} />

              {days.map((day) => (
                <TableCell key={day} className={className}>
                  <Checkbox
                    checked={_.get(values, `${path}.hours[${j}].${day}`)}
                    className={className}
                    disabled={isSubmitting || readOnly}
                    // inputProps={ { id: `${id}-${i}-hour-${j}-${day}-checkbox` } }
                    inputProps={{ id: `yy-${i}-hour-${j}-${day}-checkbox` }}
                    name={`${path}.hours[${j}].${day}`}
                    onBlur={handleBlur}
                    onChange={handleChange}
                    data-testid={`plan-elements-yy-${i}-hour-${j}-${day}-checkbox`}
                  />
                </TableCell>
              ))}

              <TableCell className={className}>
                <IconButton
                  className={`${className}${readOnly || _.get(values, `${path}.hours.length`) <= 1 ? ' hidden' : ''}`}
                  disabled={isSubmitting || readOnly || _.get(values, `${path}.hours.length`) <= 1}
                  // id={ `${id}-${i}-hour-${j}-remove-button` }
                  id={`yy-${i}-hour-${j}-remove-button`}
                  onClick={handleRemoveHour(j)}
                  data-testid={`plan-elements-yy-${i}-hour-${j}-remove-button`}
                >
                  <RemoveCircleOutlineIcon className={className} color={isSubmitting ? 'inherit' : 'primary'} />
                </IconButton>
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
      {!readOnly && (
        <Fragment>
          <FormHelperText className={className}>
            {typeof _.get(errors, `${path}.hours`) === 'string' ? _.get(errors, `${path}.hours`) : null}
          </FormHelperText>

          <Button
            className={className}
            disabled={isSubmitting}
            // id={ `${id}-${i}-hours-add-button` }
            id={`yy-${i}-hours-add-button`}
            onClick={handleAddHour}
            data-testid={`yy-${i}-hours-add-button`}
          >
            <AddCircleOutlineIcon className={className} color={isSubmitting ? 'inherit' : 'primary'} />
            &nbsp;Add
          </Button>
        </Fragment>
      )}
    </Styled>
  );
};
